import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';

/* ── Load Razorpay script dynamically ── */
function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

const FEATURES = [
    'Lo Shu Grid with full energy plane analysis',
    'Rajyog & Golden Yog auto-detection (16 combinations)',
    'Vedic Navagraha Planetary Grid',
    'Mulank & Bhagyank calculations',
    'Personalised Missing Number Remedies',
    'Lucky & Enemy Numbers with Colour Palettes',
    'Vimshottari Dasha Chart (120-year timeline)',
    'Reports in 18+ languages incl. Hindi, Marathi',
    'PDF Export & WhatsApp Sharing',
    'Lifetime access — no recurring fees',
];

export default function BuySection() {
    const { user, isSubscribed, refreshSubscription } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Pricing state (fetched from backend)
    const [pricing, setPricing] = useState({
        toolName: 'Loshu Grid Calculator',
        toolPrice: 1500,
        toolOriginalPrice: 3000,
        offerLabel: '50% OFF — Limited Time',
    });
    const [priceLoaded, setPriceLoaded] = useState(false);

    // Coupon state
    const [couponInput, setCouponInput] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discountedPrice, savings }
    const [couponError, setCouponError] = useState('');

    // Fetch live price on mount
    useEffect(() => {
        API.get('/payment/price')
            .then(({ data }) => {
                if (data.success) {
                    setPricing({
                        toolName: data.toolName,
                        toolPrice: data.toolPrice,
                        toolOriginalPrice: data.toolOriginalPrice,
                        offerLabel: data.offerLabel,
                    });
                }
            })
            .catch(() => { /* use defaults */ })
            .finally(() => setPriceLoaded(true));
    }, []);

    const effectivePrice = appliedCoupon ? appliedCoupon.discountedPrice : pricing.toolPrice;
    const savings = pricing.toolOriginalPrice - effectivePrice;

    const handleApplyCoupon = async () => {
        if (!user) { toast.error('Please sign in first.'); navigate('/login'); return; }
        if (!couponInput.trim()) return;
        setCouponLoading(true);
        setCouponError('');
        setAppliedCoupon(null);
        try {
            const { data } = await API.post('/payment/apply-coupon', { code: couponInput.trim() });
            if (data.success) {
                setAppliedCoupon({
                    code: data.coupon.code,
                    discountedPrice: data.discountedPrice,
                    savings: data.savings,
                });
                toast.success(`✅ Coupon applied! You save ₹${data.savings}`);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Invalid coupon code.';
            setCouponError(msg);
            toast.error(msg);
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponInput('');
        setCouponError('');
    };

    const handleBuy = async () => {
        if (!user) {
            toast.error('Please sign in first to purchase.');
            navigate('/login');
            return;
        }

        if (isSubscribed()) {
            toast.success('You already have access! 🎉');
            navigate('/dashboard');
            return;
        }

        const loaded = await loadRazorpayScript();
        if (!loaded) {
            toast.error('Could not load payment gateway. Check internet connection.');
            return;
        }

        setLoading(true);
        try {
            const { data } = await API.post('/payment/create-order', {
                couponCode: appliedCoupon?.code || null,
            });
            if (!data.success) throw new Error(data.message);

            const options = {
                key: data.key,
                amount: data.order.amount,
                currency: data.order.currency,
                name: 'Aakriti\'s Healverse',
                description: `${data.toolName} — Lifetime Access`,
                image: 'https://aakritishealverse.com/cdn/shop/files/Healverse_logo_1.png',
                order_id: data.order.id,
                prefill: {
                    name: data.user.name,
                    email: data.user.email,
                    contact: data.user.phone,
                },
                theme: { color: '#cead80' },
                handler: async (response) => {
                    try {
                        const verify = await API.post('/payment/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            couponCode: appliedCoupon?.code || null,
                            finalPrice: data.finalPrice,
                        });
                        if (verify.data.success) {
                            await refreshSubscription();
                            toast.success('🎉 Payment successful! Welcome to the tool!');
                            navigate('/dashboard');
                        } else {
                            toast.error('Payment verification failed. Contact support.');
                        }
                    } catch {
                        toast.error('Verification error. Contact support with your payment ID.');
                    }
                },
                modal: {
                    ondismiss: () => {
                        setLoading(false);
                        toast('Payment cancelled.', { icon: '⚠️' });
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to start payment. Try again.');
        } finally {
            setLoading(false);
        }
    };

    if (isSubscribed && isSubscribed()) return null;

    return (
        <section id="buy-section" style={{ position: 'relative', zIndex: 1, padding: '80px 24px 100px' }}>
            {/* Background glow */}
            <div style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(ellipse at 50% 50%, rgba(206,173,128,0.06) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            <div style={{ maxWidth: 1050, margin: '0 auto', position: 'relative' }}>

                {/* Section header */}
                <div style={{ textAlign: 'center', marginBottom: 56 }}>
                    <span style={{
                        display: 'inline-block', marginBottom: 14,
                        background: 'rgba(206,173,128,0.12)', border: '1px solid rgba(206,173,128,0.35)',
                        borderRadius: 50, padding: '6px 20px',
                        fontSize: 11, fontWeight: 800, color: '#cead80',
                        letterSpacing: 1.5, textTransform: 'uppercase',
                    }}>Limited Time Offer</span>
                    <h2 style={{
                        fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: 900,
                        fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: 14,
                        color: '#f1f5f9', lineHeight: 1.1,
                    }}>
                        Get <span style={{
                            background: 'linear-gradient(135deg, #e8d4a8, #cead80)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>Lifetime Access</span><br />to {pricing.toolName}
                    </h2>
                    <p style={{ color: '#64748b', fontSize: 16, maxWidth: 520, margin: '0 auto' }}>
                        One-time payment. No subscriptions. Use the tool forever.
                    </p>
                </div>

                {/* Main card */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))',
                    gap: 0,
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                    border: '2px solid rgba(206,173,128,0.3)',
                    borderRadius: 28,
                    overflow: 'hidden',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(206,173,128,0.08)',
                }}>

                    {/* Left — Features */}
                    <div style={{ padding: '40px 36px', borderRight: '1px solid rgba(206,173,128,0.12)' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 20 }}>
                            Everything included:
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {FEATURES.map((f, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    padding: '10px 14px', borderRadius: 10,
                                    background: 'rgba(206,173,128,0.04)',
                                    border: '1px solid rgba(206,173,128,0.08)',
                                }}>
                                    <div style={{
                                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                                        background: 'rgba(206,173,128,0.15)',
                                        border: '1.5px solid rgba(206,173,128,0.4)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 9, color: '#cead80', fontWeight: 900, marginTop: 1,
                                    }}>✓</div>
                                    <span style={{ fontSize: 13.5, color: '#94a3b8', lineHeight: 1.6 }}>{f}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right — Pricing & CTA */}
                    <div style={{
                        padding: '40px 36px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        background: 'linear-gradient(160deg, rgba(206,173,128,0.08), transparent)',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        {/* Glow blob */}
                        <div style={{
                            position: 'absolute', top: -80, right: -80,
                            width: 250, height: 250, borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(206,173,128,0.12), transparent 70%)',
                            pointerEvents: 'none',
                        }} />

                        {/* Offer badge */}
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 28,
                            background: 'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(239,68,68,0.06))',
                            border: '1.5px solid rgba(239,68,68,0.4)',
                            borderRadius: 12, padding: '8px 18px', width: 'fit-content',
                        }}>
                            <div style={{
                                width: 8, height: 8, borderRadius: '50%', background: '#ef4444',
                                animation: 'pulse-glow 1.5s ease-in-out infinite',
                            }} />
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#f87171', letterSpacing: 0.5 }}>
                                {pricing.offerLabel}
                            </span>
                        </div>

                        {/* Price block */}
                        <div style={{ marginBottom: 24 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
                                <span style={{
                                    fontSize: appliedCoupon ? 60 : 72, fontWeight: 900, lineHeight: 1,
                                    background: 'linear-gradient(135deg, #e8d4a8, #cead80)',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    transition: 'font-size 0.3s ease',
                                }}>
                                    ₹{priceLoaded ? effectivePrice.toLocaleString('en-IN') : '...'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 18, color: '#475569', fontWeight: 600, textDecoration: 'line-through' }}>
                                    ₹{pricing.toolOriginalPrice.toLocaleString('en-IN')}
                                </span>
                                <span style={{
                                    background: 'rgba(52,211,153,0.15)',
                                    border: '1px solid rgba(52,211,153,0.3)',
                                    color: '#34d399', fontSize: 12, fontWeight: 800,
                                    padding: '3px 10px', borderRadius: 50,
                                }}>You save ₹{savings.toLocaleString('en-IN')}</span>
                            </div>
                            <div style={{ marginTop: 10, fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>♾️</span>
                                <span>One-time payment &nbsp;·&nbsp; Lifetime access &nbsp;·&nbsp; No renewals</span>
                            </div>
                        </div>

                        {/* ── Coupon Code Section ── */}
                        <div style={{ marginBottom: 24 }}>
                            {!appliedCoupon ? (
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                                        🎟 Have a coupon code?
                                    </label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="text"
                                            placeholder="Enter coupon code"
                                            value={couponInput}
                                            onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                                            onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                                            style={{
                                                flex: 1, padding: '11px 16px',
                                                background: 'rgba(255,255,255,0.05)',
                                                border: `1.5px solid ${couponError ? 'rgba(239,68,68,0.5)' : 'rgba(206,173,128,0.25)'}`,
                                                borderRadius: 10, color: '#f1f5f9',
                                                fontSize: 14, fontWeight: 700,
                                                letterSpacing: 1, outline: 'none',
                                                fontFamily: 'monospace',
                                                transition: 'border-color 0.2s',
                                            }}
                                            onFocus={e => e.target.style.borderColor = 'rgba(206,173,128,0.6)'}
                                            onBlur={e => e.target.style.borderColor = couponError ? 'rgba(239,68,68,0.5)' : 'rgba(206,173,128,0.25)'}
                                        />
                                        <button
                                            onClick={handleApplyCoupon}
                                            disabled={couponLoading || !couponInput.trim()}
                                            style={{
                                                padding: '11px 18px', borderRadius: 10, border: '1.5px solid rgba(206,173,128,0.4)',
                                                background: 'transparent', color: '#cead80',
                                                fontWeight: 700, fontSize: 13, cursor: couponLoading ? 'not-allowed' : 'pointer',
                                                whiteSpace: 'nowrap', transition: 'all 0.2s',
                                                opacity: (!couponInput.trim() || couponLoading) ? 0.5 : 1,
                                            }}
                                        >
                                            {couponLoading ? '...' : 'Apply'}
                                        </button>
                                    </div>
                                    {couponError && (
                                        <p style={{ fontSize: 12, color: '#f87171', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            ❌ {couponError}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div style={{
                                    padding: '12px 16px', borderRadius: 12,
                                    background: 'rgba(52,211,153,0.08)',
                                    border: '1.5px solid rgba(52,211,153,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 14, fontWeight: 800, color: '#34d399', fontFamily: 'monospace', letterSpacing: 1 }}>
                                                🎟 {appliedCoupon.code}
                                            </span>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, color: '#34d399',
                                                background: 'rgba(52,211,153,0.15)',
                                                border: '1px solid rgba(52,211,153,0.3)',
                                                padding: '2px 8px', borderRadius: 50,
                                            }}>APPLIED</span>
                                        </div>
                                        <p style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                                            You save ₹{appliedCoupon.savings.toLocaleString('en-IN')} with this coupon
                                        </p>
                                    </div>
                                    <button onClick={handleRemoveCoupon} style={{
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        color: '#64748b', fontSize: 18, lineHeight: 1,
                                        padding: 4, borderRadius: 6,
                                    }}>✕</button>
                                </div>
                            )}
                        </div>

                        {/* Trust badges */}
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
                            {['🔒 Secure Payment', '✅ Instant Access', '📞 Support Included'].map(b => (
                                <span key={b} style={{
                                    fontSize: 11, fontWeight: 600, color: '#64748b',
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 8, padding: '5px 10px',
                                }}>{b}</span>
                            ))}
                        </div>

                        {/* CTA button */}
                        <button
                            onClick={handleBuy}
                            disabled={loading}
                            id="buy-now-btn"
                            style={{
                                width: '100%', padding: '18px 24px',
                                borderRadius: 16, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                                background: loading
                                    ? 'rgba(206,173,128,0.3)'
                                    : 'linear-gradient(135deg, #a07e4a, #cead80, #e8d4a8)',
                                color: '#1a0a00', fontSize: 17, fontWeight: 900,
                                boxShadow: loading ? 'none' : '0 8px 32px rgba(206,173,128,0.4)',
                                transition: 'all 0.3s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                letterSpacing: 0.3,
                                animation: !loading ? 'pulse-glow 2.5s ease-in-out infinite' : 'none',
                            }}
                            onMouseEnter={e => {
                                if (!loading) {
                                    e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                                    e.currentTarget.style.boxShadow = '0 14px 48px rgba(206,173,128,0.55)';
                                }
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                e.currentTarget.style.boxShadow = '0 8px 32px rgba(206,173,128,0.4)';
                            }}
                        >
                            {loading ? (
                                <>
                                    <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: 18 }}>⟳</span>
                                    Processing…
                                </>
                            ) : (
                                <>Buy Now — ₹{effectivePrice.toLocaleString('en-IN')} Only</>
                            )}
                        </button>

                        {/* Razorpay badge */}
                        <div style={{
                            marginTop: 16, textAlign: 'center',
                            fontSize: 12, color: '#334155',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        }}>
                            <span>Secured by</span>
                            <span style={{ fontWeight: 800, color: '#475569' }}>Razorpay</span>
                            <span>· UPI · Cards · Net Banking</span>
                        </div>


                    </div>
                </div>

                {/* Bottom trust strip */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 36, flexWrap: 'wrap' }}>
                    {[
                        { icon: '⭐', label: '4.9/5 Rating', sub: 'from 200+ users' },
                        { icon: '🔐', label: '256-bit Encryption', sub: 'bank-grade security' },
                        { icon: '⚡', label: 'Instant Access', sub: 'right after payment' },
                        { icon: '🌐', label: '18+ Languages', sub: 'including Hindi' },
                    ].map(({ icon, label, sub }) => (
                        <div key={label} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>{label}</div>
                            <div style={{ fontSize: 11, color: '#475569' }}>{sub}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
